'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { Card } from '@/components/layout';
import { useSiteLanguage } from '@/components/common/SiteLanguageProvider';

export default function ScheduleCallCard() {
  const [isExpanded, setIsExpanded] = useState(false);
  const { language } = useSiteLanguage();

  const copy = {
    en: {
      title: 'Schedule a Call',
      cta: 'Book a Digitization Call',
      duration: '30-minute consultation',
      clickToCall: 'Tap to discuss what to digitize',
    },
    fr: {
      title: 'Planifier un appel',
      cta: 'Reserver un appel de numerisation',
      duration: 'Consultation de 30 minutes',
      clickToCall: 'Appelez pour parler de numerisation',
    },
  } as const;

  const t = copy[language];

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Card
      className={`p-6 text-center transition-all duration-300 ease-out cursor-pointer ${
        isExpanded ? 'border-primary shadow-neon-cyan' : 'hover:border-primary/50 hover:shadow-neon-cyan/50'
      } active:scale-[0.98]`}
      onClick={toggleExpanded}
      role="button"
      tabIndex={0}
      aria-expanded={isExpanded}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleExpanded();
        }
      }}
    >
      <div
        className={`inline-flex p-4 rounded-lg mb-4 transition-all duration-300 ease-out ${
          isExpanded ? 'bg-primary/20 scale-110' : 'bg-primary/10'
        }`}
      >
        <Icon
          name="CalendarIcon"
          size={32}
          className={`text-primary transition-transform duration-300 ease-out ${
            isExpanded ? 'rotate-180' : 'rotate-0'
          }`}
        />
      </div>

      <h3 className="font-sans text-card-title text-foreground mb-2">{t.title}</h3>

      <div
        className={`transition-all duration-200 ease-out ${
          isExpanded ? 'opacity-0 max-h-0 overflow-hidden' : 'opacity-100 max-h-20'
        }`}
      >
        <p className="text-primary font-medium">{t.cta}</p>
        <p className="text-sm text-muted-foreground mt-2">{t.duration}</p>
      </div>

      <div
        className={`transition-all ease-out ${
          isExpanded ? 'opacity-100 translate-y-0 max-h-32' : 'opacity-0 translate-y-5 max-h-0 overflow-hidden'
        }`}
        style={{
          transitionDuration: '400ms',
          transitionDelay: isExpanded ? '150ms' : '0ms',
        }}
      >
        <a
          href="tel:+16476179326"
          className="inline-flex items-center gap-3 font-sans text-card-title font-medium text-primary hover:text-primary/80 transition-colors duration-200 group/phone whitespace-nowrap"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-2 bg-primary/10 rounded-lg group-hover/phone:bg-primary/20 transition-colors duration-200 flex-shrink-0">
            <Icon name="DevicePhoneMobileIcon" size={24} className="text-primary" />
          </div>
          <span className="tracking-wide">+1 (647) 617-9326</span>
        </a>
        <p className="text-sm text-muted-foreground mt-3">{t.clickToCall}</p>
      </div>
    </Card>
  );
}
